import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HVAConversationProvider } from './FineTuningPlayground/HVAFineTuningPlaygroundContext';
import AgentConfigColumn from './FineTuningPlayground/HVAFineTuningPlaygroundAgentConfig';
import TestingDataForm from './FineTuningPlayground/HVAFineTuningPlaygroundDataForm';
import HVADynamicConversationWidget from './FineTuningPlayground/HVAFineTuningPlaygroundWidget';
import { FeedbackMessage } from './FineTuningPlayground/HVAFineTuningPlaygroundContext';
import { getAgentSampleData } from 'services/hvaagent.service';
import { useHVAConversation } from './FineTuningPlayground/HVAFineTuningPlaygroundContext';
import {
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
  faRefresh
} from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faCog,
  faDatabase,
  faPlay
} from '@fortawesome/free-solid-svg-icons';
import Avatar from 'components/base/Avatar';
import { Row, Col, Breadcrumb, Card, Form } from 'react-bootstrap';
import HVASpinner from 'components/base/HVASpinner';

// Datos de ejemplo por defecto
const DEFAULT_BASE_SAMPLE_DATA = {
  companyname: 'ZannaBank',
  customername: 'Juan Pérez',
  customerphone: '+34600000000',
  customertrn: '51456789L',
  customerreference: 'AL12345612',
  customerdob: '1986-07-18',
  customerfathername: 'Antonio',
  customeraddress: 'Calle Asturias 123, 28001 Madrid, España',
  customeremail: 'juan.ejemplo@email.com',
  customeriscompany: 0,
  importid: '2',
  recordid: '3'
};

const DEFAULT_CALL_CONTEXT_SAMPLE = {
  debtAmount: 765,
  currency: 'EUR',
  origin: 'recibos impagados de octubre y noviembre de su préstamo personal',
  loanAmount: 12000,
  installmentAmount: 250,
  productType: 'Préstamo Personal',
  loanTerm: 48,
  remainingTerm: 36
};

const DEFAULT_BXP_CONTEXT_SAMPLE = {
  ANI: '326667',
  DNIS: '5555',
  channel: 'phone',
  queue: 'collections',
  priority: 'high',
  waitTime: 45
};

const HVAAgentFineTuningContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Hook del contexto
  const {
    setConversationResults: setContextConversationResults,
    conversationResults: contextConversationResults
  } = useHVAConversation();

  // Sincronizar desde el contexto hacia el estado local
  useEffect(() => {
    setConversationResults(contextConversationResults || '');
  }, [contextConversationResults]);

  // Recuperar datos del state
  const { agentId, agentName, campaignId, agentImage, title } =
    location.state || {};

  // Estado para controlar qué secciones están expandidas
  const [expandedSections, setExpandedSections] = useState({
    configuration: true,
    testData: true,
    playground: true
  });

  // Estados para manejar feedback y datos
  const [feedbackMessages, setFeedbackMessages] = useState<FeedbackMessage[]>(
    []
  );
  const [conversationResults, setConversationResults] = useState('');
  const [isLoadingSampleData, setIsLoadingSampleData] = useState(false);
  const [sampleData, setSampleData] = useState({
    baseSampleData: DEFAULT_BASE_SAMPLE_DATA,
    callContextSample: DEFAULT_CALL_CONTEXT_SAMPLE,
    bxpContextSample: DEFAULT_BXP_CONTEXT_SAMPLE
  });

  // Estado para el tipo de llamada
  const [callType, setCallType] = useState<'Outbound' | 'Inbound'>('Outbound');

  // Referencia para el contenedor de mensajes
  const feedbackContainerRef = useRef<HTMLDivElement>(null);

  // Verificar que tenemos los datos necesarios
  useEffect(() => {
    if (!agentId) {
      // Si no hay datos, regresar a la lista
      navigate('/system/conversationalengine/agents');
    }
  }, [agentId, navigate]);

  const handleBackToAgents = () => {
    navigate('/system/conversationalengine/agentmanagement');
  };

  const toggleSection = (
    section: 'configuration' | 'testData' | 'playground'
  ) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Cargar datos de ejemplo del agente desde la BD
  useEffect(() => {
    const loadSampleData = async () => {
      if (!agentId) {
        return;
      }

      try {
        setIsLoadingSampleData(true);

        const response = await getAgentSampleData(agentId);

        if (response.hasSampleData && response.sampleData) {
          // Función para parsear datos de forma robusta
          const parseSampleData = (data: object | string | null) => {
            try {
              // Si ya es un objeto, devolverlo tal cual
              if (typeof data === 'object' && data !== null) {
                return data;
              }

              // Si es string, intentar parsear
              if (typeof data === 'string') {
                return JSON.parse(data);
              }
              return {};
            } catch (error) {
              console.error(
                '❌ [LoadSampleData] Error parseando datos:',
                error
              );
              return {};
            }
          };

          const parsedSampleData = parseSampleData(response.sampleData);

          const newSampleData = {
            baseSampleData:
              parsedSampleData.baseSampleData || DEFAULT_BASE_SAMPLE_DATA,
            callContextSample:
              parsedSampleData.callContextSample || DEFAULT_CALL_CONTEXT_SAMPLE,
            bxpContextSample:
              parsedSampleData.bxpContextSample || DEFAULT_BXP_CONTEXT_SAMPLE
          };

          setSampleData(newSampleData);
        }
      } catch (error) {
        console.error('❌ [LoadSampleData] Error:', error);
        // Mantener los valores por defecto en caso de error
      } finally {
        setIsLoadingSampleData(false);
      }
    };

    loadSampleData();
  }, [agentId]);

  // Efecto para hacer scroll al último mensaje de feedback
  useEffect(() => {
    if (feedbackContainerRef.current && feedbackMessages.length > 0) {
      // Hacer scroll hasta el final del contenedor
      feedbackContainerRef.current.scrollTop =
        feedbackContainerRef.current.scrollHeight;
    }
  }, [feedbackMessages]);

  // Función para añadir mensajes de feedback
  const addFeedbackMessage = (
    message: Omit<FeedbackMessage, 'id' | 'timestamp'>
  ) => {
    setFeedbackMessages(prev => [
      ...prev.slice(0, 19), // Mantener las últimas 19 notificaciones
      {
        ...message,
        id: Math.random().toString(36).substring(2, 11),
        timestamp: new Date()
      }
    ]);
  };

  // Función para manejar errores
  const handleComponentError = (error: Error) => {
    const errorMessage = error?.message || 'Error desconocido';

    addFeedbackMessage({
      type: 'info',
      message: 'Critical error',
      details: errorMessage,
      success: false
    });
  };

  // Función para manejar actualizaciones de configuración
  const handleConfigUpdateSuccess = (
    field:
      | 'firstMessage'
      | 'inboundFirstMessage'
      | 'prompt'
      | 'inboundprompt'
      | 'callresultprompt'
      | 'tools'
  ) => {
    const messages = {
      firstMessage: 'Outbound greeting updated',
      inboundFirstMessage: 'Inbound greeting updated',
      prompt: 'Outbound Prompt updated',
      inboundprompt: 'Inbound Prompt updated',
      callresultprompt: 'Call End Prompt updated',
      tools: 'Tools configuration updated'
    };

    addFeedbackMessage({
      type: 'info',
      message: messages[field],
      success: true
    });
  };

  // Manejador para feedback del formulario
  const handleFormFeedback = (
    message: string,
    details?: string,
    success: boolean = true
  ) => {
    addFeedbackMessage({
      type: 'info',
      message,
      details,
      success
    });
  };

  // Si no hay datos, mostrar loading o regresar
  if (!agentId) {
    return <div>Loading...</div>;
  }

  // Función para preparar los datos completos del formulario
  const prepareFormInitialData = () => {
    return {
      // Campos básicos adaptados al formato del formulario
      companyName: sampleData.baseSampleData.companyname || '',
      customerName: sampleData.baseSampleData.customername || '',
      customerPhone: sampleData.baseSampleData.customerphone || '',
      customerTrn: sampleData.baseSampleData.customertrn || '',
      customerReference: sampleData.baseSampleData.customerreference || '',
      customerDob: sampleData.baseSampleData.customerdob || '',
      customerFatherName: sampleData.baseSampleData.customerfathername || '',
      customerAddress: sampleData.baseSampleData.customeraddress || '',
      customerEmail: sampleData.baseSampleData.customeremail || '',
      customerIsCompany: sampleData.baseSampleData.customeriscompany || 0,
      importId: sampleData.baseSampleData.importid || '',
      recordId: sampleData.baseSampleData.recordid || '',

      // El contexto adicional como un JSON
      additionalContext: JSON.stringify(
        {
          callContext: sampleData.callContextSample,
          bxpcontext: sampleData.bxpContextSample
        },
        null,
        2
      )
    };
  };

  return (
    <div className="p-0 mt-n4">
      {/* Breadcrumb de navegación */}
      <Breadcrumb className="mb-1">
        <Breadcrumb.Item
          onClick={handleBackToAgents}
          style={{ cursor: 'pointer' }}
        >
          All Agents
        </Breadcrumb.Item>
        <Breadcrumb.Item active>
          {agentName || 'Agent Fine-tuning'}
        </Breadcrumb.Item>
      </Breadcrumb>

      {/* Header Principal con info del agente */}
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="mb-1">Agent Fine-tuning: {agentName}</h2>
          <p className="text-muted mb-0">
            Campaign: {campaignId} | Agent: {agentId}
          </p>
          {title && <p className="text-muted mb-0">{title}</p>}
        </Col>
        <Col xs="auto">
          {agentImage && (
            <Avatar
              size="4xl"
              src={agentImage}
              alt={agentName}
              variant="image"
              rounded="circle"
              imageClassName="avatar-image-top border shadow-sm bg-body-emphasis"
            />
          )}
        </Col>
      </Row>

      {/* Sección 1: Configuración del Agente */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-secondary-subtle p-0">
          <div
            onClick={() => toggleSection('configuration')}
            style={{ cursor: 'pointer', padding: '1rem' }}
            className="d-block w-100"
          >
            <Row className="align-items-center">
              <Col>
                <h4 className="mb-0">
                  <FontAwesomeIcon icon={faCog} className="me-2" />
                  Agent Configuration
                </h4>
                <p className="mb-0 text-muted fs-9 px-4">
                  Configure prompts and tools for the agent
                </p>
              </Col>
              <Col xs="auto">
                <FontAwesomeIcon
                  icon={
                    expandedSections.configuration ? faChevronUp : faChevronDown
                  }
                  className="text-primary fs-9"
                />
              </Col>
            </Row>
          </div>
        </Card.Header>
        {expandedSections.configuration && (
          <Card.Body className="px-6">
            <AgentConfigColumn
              agentId={agentId}
              onUpdateSuccess={handleConfigUpdateSuccess}
              onError={handleComponentError}
            />
          </Card.Body>
        )}
      </Card>

      {/* Sección 2: Datos de Prueba */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-secondary-subtle p-0">
          <div
            onClick={() => toggleSection('testData')}
            style={{ cursor: 'pointer', padding: '1rem' }}
            className="d-block w-100"
          >
            <Row className="align-items-center">
              <Col>
                <h4 className="mb-0">
                  <FontAwesomeIcon icon={faDatabase} className="me-2" />
                  Test Data Configuration
                </h4>
                <p className="mb-0 text-muted fs-9 px-4">
                  Set up test scenarios for agent conversations
                </p>
              </Col>
              <Col xs="auto">
                <FontAwesomeIcon
                  icon={expandedSections.testData ? faChevronUp : faChevronDown}
                  className="text-primary fs-9"
                />
              </Col>
            </Row>
          </div>
        </Card.Header>
        {expandedSections.testData && (
          <Card.Body className="px-6">
            {isLoadingSampleData ? (
              <HVASpinner />
            ) : (
              <TestingDataForm
                agentId={agentId}
                initialData={prepareFormInitialData()}
                onFeedback={handleFormFeedback}
              />
            )}
          </Card.Body>
        )}
      </Card>

      {/* Sección 3: Playground */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-secondary-subtle p-0">
          <div
            onClick={() => toggleSection('playground')}
            style={{ cursor: 'pointer', padding: '1rem' }}
            className="d-block w-100"
          >
            <Row className="align-items-center">
              <Col>
                <h4 className="mb-0">
                  <FontAwesomeIcon icon={faPlay} className="me-2" />
                  Playground & Testing
                </h4>
                <p className="mb-0 text-muted fs-9 px-4">
                  Test agent conversations and monitor responses
                </p>
              </Col>
              <Col xs="auto">
                <FontAwesomeIcon
                  icon={
                    expandedSections.playground ? faChevronUp : faChevronDown
                  }
                  className="text-primary fs-9"
                />
              </Col>
            </Row>
          </div>
        </Card.Header>
        {expandedSections.playground && (
          <Card.Body className="px-6">
            <Row>
              <Col lg={6}>
                {/* Widget de conversación */}
                <div translate="no">
                  <div className="d-flex justify-content-start align-items-center mb-0">
                    <h6 className="mb-0 me-1">Test</h6>
                    <div className="m-0 p-0 px-3 pt-1 bg-body border rounded-5">
                      <Form.Check
                        type="switch"
                        id="call-type-switch"
                        label={callType === 'Outbound' ? 'Outbound' : 'Inbound'}
                        checked={callType === 'Inbound'}
                        onChange={e =>
                          setCallType(e.target.checked ? 'Inbound' : 'Outbound')
                        }
                        className="mb-0 pb-0 fs-9"
                      />
                    </div>
                    <h6 className="mb-0 mx-1">Call</h6>
                  </div>
                  <HVADynamicConversationWidget
                    agentId={agentId}
                    agentImage={agentImage}
                    campaignId={campaignId}
                    callType={callType}
                    onError={handleComponentError}
                    onFeedback={addFeedbackMessage}
                  />
                </div>
              </Col>
              <Col lg={6} style={{ paddingTop: '69px' }} translate="no">
                <h6 className="mb-2">Agent Feedback</h6>
                <div
                  ref={feedbackContainerRef}
                  className="p-4 border rounded scrollbar bg-body"
                  style={{
                    height: '310px',
                    overflowY: 'auto'
                  }}
                  translate="no"
                >
                  {feedbackMessages.length > 0 ? (
                    feedbackMessages.map(msg => (
                      <div
                        key={msg.id}
                        className={`alert ${
                          msg.success
                            ? 'bg-body-emphasis'
                            : 'bg-warning-lighter'
                        } mb-2 py-2 px-3`}
                        role="alert"
                      >
                        <div className="d-flex flex-column">
                          <small className="text-muted fs-10">
                            {msg.timestamp.toLocaleTimeString()}
                          </small>
                          <div className="fs-9 text-primary lh-1 d-flex align-items-center">
                            <FontAwesomeIcon
                              icon={
                                msg.success
                                  ? faCheckCircle
                                  : faExclamationTriangle
                              }
                              className={`me-2 ${
                                msg.success ? 'text-success' : 'text-danger'
                              }`}
                            />
                            {msg.message}
                          </div>
                          {msg.details && (
                            <small className="mt-1 text-muted fs-10 lh-1">
                              {msg.details}
                            </small>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-body-quaternary py-5 fs-9">
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="mb-2 fs-7"
                      />
                      <p>No feedback messages</p>
                    </div>
                  )}
                </div>

                {/* Resultados de la conversación */}
                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Conversation Results</h6>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => {
                        setConversationResults('');
                        setContextConversationResults(''); // Limpiar también el contexto
                      }}
                      className="p-0"
                    >
                      <FontAwesomeIcon icon={faRefresh} /> Clear
                    </Button>
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={9}
                    value={conversationResults}
                    readOnly
                    placeholder="Results will appear here after the call"
                    className="scrollbar bg-body shadow-none"
                    translate="no"
                  />
                </div>
              </Col>
            </Row>
          </Card.Body>
        )}
      </Card>
    </div>
  );
};

// Componente principal que provee el contexto
const HVAAgentFineTuningPage: React.FC = () => {
  return (
    <HVAConversationProvider>
      <HVAAgentFineTuningContent />
    </HVAConversationProvider>
  );
};

export default HVAAgentFineTuningPage;
